/**
 * Recording what happened on a call.
 *
 * The state machine already exists — `getFollowUpCategory` reads the date,
 * `promiseState` reads the promise, and the stored `status` says only whether
 * somebody declared the money collected. This file is about the *action*: what
 * a person is allowed to submit, and what the form should suggest next.
 *
 * Nothing here decides authorization. `record_follow_up()` in migration 0013
 * checks `followups.record` and `can_see_customer()` with the caller's own
 * identity, in one transaction with the write. These rules exist so that a
 * person is not offered a form whose submission the database will refuse.
 */
import type { ActivityKind } from './activity.js';
import { calendarDay } from './format.js';

/** The outcomes a person may record. `system` is not among them. */
export type FollowUpOutcome = Exclude<ActivityKind, 'system'>;

export interface OutcomeChoice {
    kind: FollowUpOutcome;
    label: string;
    /** What it means, in the words somebody working a call list would use. */
    hint: string;
    /** Whether the form should ask for an amount and a date. */
    needsPromise?: boolean;
    /** Days from today the next follow-up is suggested for. */
    suggestDays: number | null;
}

/**
 * Short on purpose.
 *
 * Somebody working through forty accounts will not choose from twenty options.
 * Anything that does not fit is a note, which is why `note` is first.
 */
export const OUTCOME_CHOICES: readonly OutcomeChoice[] = [
    { kind: 'note', label: 'Note', hint: 'Anything worth writing down.', suggestDays: null },
    { kind: 'no_answer', label: 'No answer', hint: 'Rang out, or nobody available.', suggestDays: 1 },
    { kind: 'declined', label: 'Call declined', hint: 'They cut the call.', suggestDays: 3 },
    { kind: 'promise', label: 'Promised to pay', hint: 'They committed to an amount by a date.', needsPromise: true, suggestDays: null },
    { kind: 'payment', label: 'Payment received', hint: 'Money actually arrived.', suggestDays: null },
    { kind: 'visit', label: 'Visited', hint: 'Somebody went in person.', suggestDays: 7 },
    { kind: 'dispute', label: 'Disputed', hint: 'They are contesting the amount.', suggestDays: 2 },
];

export const outcomeChoice = (kind: FollowUpOutcome): OutcomeChoice | undefined =>
    OUTCOME_CHOICES.find(c => c.kind === kind);

/** What a person has filled in. Dates are `YYYY-MM-DD`, as an input gives them. */
export interface FollowUpDraft {
    kind: FollowUpOutcome;
    body: string;
    /** The next follow-up. Empty means "leave it as it is"; see `clearFollowUp`. */
    nextFollowUp: string;
    clearFollowUp: boolean;
    promisedAmount: string;
    promisedOn: string;
    /** Declaring the money collected. */
    completed: boolean;
}

export const emptyDraft = (kind: FollowUpOutcome = 'note'): FollowUpDraft => ({
    kind,
    body: '',
    nextFollowUp: '',
    clearFollowUp: false,
    promisedAmount: '',
    promisedOn: '',
    completed: false,
});

export interface DraftProblem {
    field: 'body' | 'nextFollowUp' | 'promisedAmount' | 'promisedOn';
    message: string;
}

const isoDay = (date: Date): string => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 10);
};

const MAX_BODY = 4000;

/**
 * Everything wrong with a draft, so it can all be fixed at once.
 *
 * A promise with no amount is the one that matters. It reads as a commitment on
 * the screen and in the morning email, it is counted in the cash-flow forecast,
 * and with no figure it commits the customer to nothing — which is worse than
 * having recorded nothing at all.
 */
export function draftProblems(draft: FollowUpDraft, today: Date = new Date()): DraftProblem[] {
    const problems: DraftProblem[] = [];
    const choice = outcomeChoice(draft.kind);

    if (draft.body.length > MAX_BODY) {
        problems.push({ field: 'body', message: `Keep this under ${MAX_BODY} characters.` });
    }
    if (draft.kind === 'note' && draft.body.trim() === '') {
        problems.push({ field: 'body', message: 'A note needs something in it.' });
    }

    if (choice?.needsPromise) {
        const amount = Number(draft.promisedAmount);
        if (draft.promisedAmount.trim() === '' || !Number.isFinite(amount) || amount <= 0) {
            problems.push({ field: 'promisedAmount', message: 'How much did they promise?' });
        }
        if (!draft.promisedOn.trim()) {
            problems.push({ field: 'promisedOn', message: 'By when?' });
        } else if (isNaN(new Date(draft.promisedOn).getTime())) {
            problems.push({ field: 'promisedOn', message: 'That is not a date.' });
        } else if (draft.promisedOn < isoDay(today)) {
            problems.push({ field: 'promisedOn', message: 'A promise cannot be for a day that has passed.' });
        }
    }

    if (draft.nextFollowUp.trim()) {
        if (isNaN(new Date(draft.nextFollowUp).getTime())) {
            problems.push({ field: 'nextFollowUp', message: 'That is not a date.' });
        } else if (draft.nextFollowUp < isoDay(today)) {
            problems.push({ field: 'nextFollowUp', message: 'Choose today or later.' });
        }
    }

    if (draft.clearFollowUp && draft.nextFollowUp.trim()) {
        problems.push({
            field: 'nextFollowUp',
            message: 'Either set a next date or remove it — not both.',
        });
    }

    return problems;
}

export const draftIsValid = (draft: FollowUpDraft, today: Date = new Date()): boolean =>
    draftProblems(draft, today).length === 0;

/**
 * What the form should offer as the next follow-up when an outcome is chosen.
 *
 * A promise answers itself: the day after they said they would pay is the day
 * to ring if it has not arrived. Everything else has a sensible gap, and a
 * payment suggests nothing — there is nothing left to chase.
 */
export function suggestedNextFollowUp(
    draft: Pick<FollowUpDraft, 'kind' | 'promisedOn'>,
    today: Date = new Date(),
): string {
    if (draft.kind === 'promise') {
        // A calendar day, read as one. `new Date('2026-12-01')` is UTC midnight,
        // which west of Greenwich is still the evening before — and the day
        // after the promise came out as the promise day itself.
        const due = calendarDay(draft.promisedOn);
        if (!due) return '';
        due.setDate(due.getDate() + 1);
        return isoDay(due);
    }

    const days = outcomeChoice(draft.kind)?.suggestDays;
    if (days === null || days === undefined) return '';
    return presetDay(days, today);
};

/**
 * The gaps somebody reaches for most, one press each, so a date is set without
 * opening a calendar — counted from the company's day, like everything else.
 */
export const DATE_PRESETS = [
    { label: 'Tomorrow', days: 1 },
    { label: 'In 3 days', days: 3 },
    { label: 'Next week', days: 7 },
] as const;

/** The words for a one-press gap, for a field that offers its own set. */
export function presetLabel(days: number): string {
    if (days === -1) return 'Yesterday';
    if (days < 0) return `${-days} days ago`;
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === 7) return 'Next week';
    if (days === 14) return 'In 2 weeks';
    return `In ${days} days`;
}

/** The calendar day `days` after `today`, as `YYYY-MM-DD`. */
export function presetDay(days: number, today: Date = new Date()): string {
    const next = new Date(today);
    next.setDate(next.getDate() + days);
    return isoDay(next);
}

/**
 * One-press amounts for "How much" on a promise, from the account's own
 * figures: the whole outstanding, the part over 90 days (the part a firm
 * reminder asks for), and half. Relative, never a fixed sum — a fixed ₹1 lakh
 * means nothing in another company or another currency. None for an account
 * in credit, which owes nothing to promise.
 */
export interface AmountPreset {
    label: string;
    amount: number;
}

const cents = (value: number): number => Math.round(value * 100) / 100;

export function promiseAmountPresets(
    account: { total: number; totalType?: 'Dr' | 'Cr' | undefined },
    over90: number,
): AmountPreset[] {
    const outstanding = Math.abs(Number(account.total) || 0);
    if (account.totalType === 'Cr' || outstanding <= 0) return [];
    const presets: AmountPreset[] = [{ label: 'Full amount', amount: cents(outstanding) }];
    if (over90 > 0 && over90 < outstanding) presets.push({ label: 'Over 90 days', amount: cents(over90) });
    presets.push({ label: 'Half', amount: cents(outstanding / 2) });
    return presets;
}

/**
 * The arguments `answer_promise()` takes (0033). Kept is recorded as a payment
 * answering the promise; not kept as a note answering it.
 */
export interface AnswerPromiseCall {
    p_workspace: string;
    p_customer: string;
    p_promise: string;
    p_kept: boolean;
    p_note: string;
    p_next_follow_up: string | null;
    p_clear_follow_up: boolean;
    p_expected_updated_at: string | null;
}

export function answerPromiseCall(
    workspaceId: string,
    customerId: string,
    promiseId: string,
    kept: boolean,
    options: { note?: string; nextFollowUp?: string; clearFollowUp?: boolean; expectedUpdatedAt?: string | null } = {},
): AnswerPromiseCall {
    const next = options.nextFollowUp?.trim();
    return {
        p_workspace: workspaceId,
        p_customer: customerId,
        p_promise: promiseId,
        p_kept: kept,
        p_note: (options.note ?? '').trim(),
        p_next_follow_up: next ? new Date(`${next}T12:00:00`).toISOString() : null,
        p_clear_follow_up: options.clearFollowUp ?? false,
        p_expected_updated_at: options.expectedUpdatedAt ?? null,
    };
}

/**
 * The arguments `record_follow_up()` takes, built from a draft.
 *
 * Built here rather than in the screen so the shape has a test. The parameter
 * names are the database's, deliberately: a mismatch is a silent default, and a
 * silent default on `p_completed` would mark an account collected.
 */
export interface FollowUpCall {
    p_workspace: string;
    p_customer: string;
    p_kind: FollowUpOutcome;
    p_body: string;
    p_next_follow_up: string | null;
    p_clear_follow_up: boolean;
    p_promised_amount: number | null;
    p_promised_on: string | null;
    p_resolves: string | null;
    p_completed: boolean | null;
    p_expected_updated_at: string | null;
}

export function followUpCall(
    workspaceId: string,
    customerId: string,
    draft: FollowUpDraft,
    options: { resolves?: string | null; expectedUpdatedAt?: string | null } = {},
): FollowUpCall {
    const wantsPromise = outcomeChoice(draft.kind)?.needsPromise === true;
    const amount = Number(draft.promisedAmount);

    return {
        p_workspace: workspaceId,
        p_customer: customerId,
        p_kind: draft.kind,
        p_body: draft.body.trim(),
        // A date input gives a local day; the account's next follow-up is a
        // moment, and midday avoids a timezone shifting it onto the day before.
        p_next_follow_up: draft.nextFollowUp.trim()
            ? new Date(`${draft.nextFollowUp}T12:00:00`).toISOString()
            : null,
        p_clear_follow_up: draft.clearFollowUp,
        p_promised_amount: wantsPromise && Number.isFinite(amount) ? amount : null,
        p_promised_on: wantsPromise && draft.promisedOn.trim() ? draft.promisedOn : null,
        p_resolves: options.resolves ?? null,
        // `null` means "leave the status alone". Only an explicit tick marks an
        // account collected, and only an explicit untick reopens one.
        p_completed: draft.completed ? true : null,
        p_expected_updated_at: options.expectedUpdatedAt ?? null,
    };
}
